import { useEffect, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { Icon } from "../components/Icon.js";
import {
  ApiError,
  deleteConnector,
  googleOAuthStartUrl,
  listConnectors,
  requestGoogleConnector,
  type Connector,
} from "../api/client.js";

const ADMIN_EMAIL = "appsdo.ind@gmail.com";

export function ConnectorsPage() {
  const [connectors, setConnectors] = useState<Connector[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [requestOpen, setRequestOpen] = useState(false);
  const [params, setParams] = useSearchParams();

  const justAdded = params.get("google_added");
  const oauthError = params.get("google_error");

  async function refresh() {
    setError(null);
    try {
      const r = await listConnectors();
      setConnectors(r.connectors);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't load connectors.");
    }
  }
  useEffect(() => {
    void refresh();
  }, []);

  function clearToast() {
    const next = new URLSearchParams(params);
    next.delete("google_added");
    next.delete("google_error");
    next.delete("expected");
    next.delete("got");
    setParams(next, { replace: true });
  }

  async function onDelete(c: Connector) {
    const label = c.email ?? c.id;
    const verb =
      c.status === "awaiting_approval"
        ? "Cancel this request for"
        : c.status === "rejected"
          ? "Remove this rejected request for"
          : "Disconnect";
    if (!confirm(`${verb} ${label}?`)) return;
    try {
      await deleteConnector(c.id);
      void refresh();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Delete failed.");
    }
  }

  function onConnect(c: Connector) {
    if (c.status !== "approved") return;
    window.location.href = googleOAuthStartUrl(c.id, "/connectors");
  }

  return (
    <div className="flex flex-col gap-lg">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-headline-md text-on-background">My calendars</h1>
          <p className="text-body-sm text-on-surface-variant mt-xs">
            Add a Gmail to start syncing. Local Outlook calendars appear here automatically once you install the
            desktop app on a PC.
          </p>
        </div>
        <button
          onClick={() => setRequestOpen(true)}
          className="bg-primary-container text-on-primary-container px-md py-sm rounded-lg text-label-xs font-mono uppercase tracking-widest flex items-center gap-sm active:scale-[0.98] transition-transform"
        >
          <Icon name="add" />
          Request Gmail
        </button>
      </div>

      {justAdded && (
        <Toast tone="ok" icon="check_circle" message={`Connected ${justAdded} successfully.`} onClose={clearToast} />
      )}
      {oauthError && (
        <Toast tone="error" icon="error" message={describeOAuthError(oauthError, params)} onClose={clearToast} />
      )}
      {error && (
        <div className="bg-error-container/20 border border-error/30 text-error rounded-lg p-3 text-body-sm flex items-center gap-2">
          <Icon name="error" />
          {error}
        </div>
      )}

      {connectors === null ? (
        <Skeleton />
      ) : connectors.length === 0 ? (
        <EmptyState onRequest={() => setRequestOpen(true)} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-gutter">
          {connectors.map((c) => (
            <ConnectorCard key={c.id} connector={c} onDelete={onDelete} onConnect={onConnect} />
          ))}
        </div>
      )}

      {requestOpen && (
        <RequestModal
          onClose={() => setRequestOpen(false)}
          onSubmitted={() => {
            setRequestOpen(false);
            void refresh();
          }}
        />
      )}
    </div>
  );
}

function ConnectorCard({
  connector,
  onDelete,
  onConnect,
}: {
  connector: Connector;
  onDelete: (c: Connector) => void;
  onConnect: (c: Connector) => void;
}) {
  const meta = providerMeta(connector.type);
  const badge = statusBadge(connector.status);
  return (
    <div className="bg-surface-container border border-outline-variant rounded-lg overflow-hidden flex flex-col">
      <div className="p-md border-b border-outline-variant bg-surface-container-high/50 flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <div className={`w-10 h-10 ${meta.iconBg} rounded flex items-center justify-center border shrink-0`}>
            <Icon name={meta.icon} className={meta.iconClass} />
          </div>
          <div className="min-w-0">
            <h3 className="text-body-base font-bold text-on-surface">{meta.label}</h3>
            <p className="text-label-xs font-mono text-on-surface-variant uppercase truncate">
              {connector.email ?? "—"}
            </p>
          </div>
        </div>
        <span className={`text-label-xs font-mono uppercase ${badge.tone} whitespace-nowrap`}>{badge.label}</span>
      </div>

      <div className="p-md space-y-2 flex-1">
        {connector.status === "awaiting_approval" && (
          <PendingPanel email={connector.email ?? ""} requestedAt={connector.requestedAt} />
        )}
        {connector.status === "approved" && (
          <div className="p-sm bg-primary-container/10 border border-primary-container/30 rounded text-body-sm text-primary-fixed-dim">
            <div className="flex items-start gap-2">
              <Icon name="check_circle" className="text-[18px] mt-0.5" />
              <div className="flex-1">
                <p className="font-semibold">Approved by admin</p>
                <p className="text-xs mt-0.5 opacity-80">Click "Connect now" to finish via Google sign-in.</p>
              </div>
            </div>
          </div>
        )}
        {connector.status === "rejected" && (
          <div className="p-sm bg-error-container/20 border border-error/30 rounded text-body-sm text-error">
            <p className="font-semibold flex items-center gap-1">
              <Icon name="block" className="text-[18px]" /> Request rejected
            </p>
            {connector.adminNotes && <p className="text-xs mt-1 opacity-80">Admin note: {connector.adminNotes}</p>}
            <p className="text-xs mt-1 opacity-80">
              You can remove this and request again, or email {ADMIN_EMAIL} to discuss.
            </p>
          </div>
        )}
        {connector.status === "active" && (
          <>
            <Row label="Connected" value={connector.approvedAt ? new Date(connector.approvedAt).toLocaleString() : "—"} />
            <Row
              label="Last sync"
              value={connector.lastSyncAt ? new Date(connector.lastSyncAt).toLocaleString() : "never"}
            />
          </>
        )}
        {connector.lastError && (
          <div className="p-2 bg-error-container/20 border border-error/30 rounded text-body-sm text-error flex items-start gap-2">
            <Icon name="warning" className="text-[18px] mt-0.5" />
            <span className="flex-1">{connector.lastError}</span>
          </div>
        )}
      </div>

      <div className="p-md pt-0 border-t border-outline-variant/30 flex items-center justify-between">
        <button
          onClick={() => onDelete(connector)}
          className="text-label-xs font-mono uppercase text-on-surface-variant hover:text-error transition-colors flex items-center gap-1"
        >
          <Icon name={connector.status === "active" ? "link_off" : "close"} className="text-[16px]" />
          {connector.status === "awaiting_approval"
            ? "Cancel request"
            : connector.status === "rejected"
              ? "Remove"
              : "Disconnect"}
        </button>
        {connector.status === "approved" && (
          <button
            onClick={() => onConnect(connector)}
            className="bg-primary-container text-on-primary-container px-3 py-1.5 rounded text-label-xs font-mono uppercase tracking-widest flex items-center gap-1 active:scale-[0.98] transition-transform"
          >
            Connect now
            <Icon name="arrow_forward" className="text-[14px]" />
          </button>
        )}
      </div>
    </div>
  );
}

function PendingPanel({ email, requestedAt }: { email: string; requestedAt: string | null }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(ADMIN_EMAIL);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked — user can still type the email */
    }
  }
  return (
    <div className="p-sm bg-tertiary-container/15 border border-tertiary-container/30 rounded text-body-sm">
      <p className="font-semibold flex items-center gap-1 text-tertiary-container">
        <Icon name="hourglass_top" className="text-[18px]" /> Waiting for admin approval
      </p>
      <p className="text-xs mt-1 text-on-surface-variant">
        The admin needs to whitelist <span className="font-mono">{email}</span> in Google Cloud and approve this
        request. Email{" "}
        <button onClick={copy} className="font-mono text-primary-fixed-dim hover:underline inline-flex items-center gap-1">
          {ADMIN_EMAIL}
          <Icon name={copied ? "check" : "content_copy"} className="text-[12px]" />
        </button>{" "}
        to nudge them.
      </p>
      {requestedAt && (
        <p className="text-[10px] font-mono text-outline mt-1">
          Requested {new Date(requestedAt).toLocaleString()}
        </p>
      )}
    </div>
  );
}

function RequestModal({ onClose, onSubmitted }: { onClose: () => void; onSubmitted: () => void }) {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await requestGoogleConnector(email.trim().toLowerCase());
      onSubmitted();
    } catch (err) {
      if (err instanceof ApiError && err.code === "already_requested") {
        setError("You've already requested or connected this email.");
      } else {
        setError(err instanceof Error ? err.message : "Couldn't file request.");
      }
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[100] bg-background/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form
        onSubmit={onSubmit}
        onClick={(e) => e.stopPropagation()}
        className="glass-card-elevated w-full max-w-md rounded-xl p-6 space-y-4"
      >
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-headline-md text-on-surface">Request Gmail connection</h2>
            <p className="text-body-sm text-on-surface-variant mt-1">
              Enter the Gmail you want to connect. The admin will whitelist it in Google Cloud, then you'll be able
              to finish via Google sign-in.
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-on-surface-variant hover:text-on-surface">
            <Icon name="close" />
          </button>
        </div>
        <div className="space-y-1.5">
          <label htmlFor="gmail-email" className="block text-label-xs font-mono text-outline uppercase tracking-widest">
            Gmail address
          </label>
          <input
            id="gmail-email"
            type="email"
            placeholder="you@gmail.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
            className="w-full bg-surface-container-lowest border border-outline-variant rounded-lg px-3 py-2 text-on-surface text-body-sm placeholder:text-outline/50 focus:outline-none focus:border-primary-container"
          />
        </div>
        {error && (
          <div className="bg-error-container/20 border border-error/30 text-error rounded-lg p-3 text-body-sm flex items-center gap-2">
            <Icon name="error" /> {error}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-label-xs font-mono uppercase text-on-surface-variant hover:text-on-surface"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="px-6 py-2 bg-primary-container text-on-primary-container rounded-lg text-label-xs font-mono uppercase tracking-widest disabled:opacity-60"
          >
            {submitting ? "Filing…" : "Submit request"}
          </button>
        </div>
      </form>
    </div>
  );
}

function EmptyState({ onRequest }: { onRequest: () => void }) {
  return (
    <div className="bg-surface-container border border-outline-variant rounded-xl p-xl flex flex-col items-center text-center gap-md min-h-[360px] justify-center">
      <div className="w-16 h-16 rounded-full bg-primary-container/10 flex items-center justify-center">
        <Icon name="cable" className="text-primary-fixed-dim" style={{ fontSize: "36px" }} />
      </div>
      <h2 className="text-headline-md text-on-surface">No calendars connected yet</h2>
      <p className="text-body-sm text-on-surface-variant max-w-md">
        Request a Gmail to get started. Admin approval is required before you can finish via Google sign-in.
      </p>
      <button
        onClick={onRequest}
        className="bg-primary-container text-on-primary-container px-lg py-sm rounded-lg text-label-xs font-mono uppercase tracking-widest flex items-center gap-sm active:scale-[0.98] transition-transform"
      >
        <Icon name="add" />
        Request Gmail
      </button>
    </div>
  );
}

function Toast({
  tone,
  icon,
  message,
  onClose,
}: {
  tone: "ok" | "error";
  icon: string;
  message: string;
  onClose: () => void;
}) {
  const cls =
    tone === "ok"
      ? "bg-primary-container/15 border-primary-container/30 text-primary-fixed-dim"
      : "bg-error-container/20 border-error/30 text-error";
  return (
    <div className={`border rounded-lg p-3 text-body-sm flex items-center justify-between gap-2 ${cls}`}>
      <div className="flex items-center gap-2">
        <Icon name={icon} />
        <span>{message}</span>
      </div>
      <button onClick={onClose} className="opacity-70 hover:opacity-100">
        <Icon name="close" />
      </button>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between items-baseline text-body-sm">
      <span className="text-label-xs font-mono text-on-surface-variant uppercase">{label}</span>
      <span className="text-on-surface font-mono">{value}</span>
    </div>
  );
}

function Skeleton() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-gutter">
      {[0, 1, 2].map((i) => (
        <div key={i} className="h-[200px] bg-surface-container border border-outline-variant rounded-lg animate-pulse" />
      ))}
    </div>
  );
}

function statusBadge(status: Connector["status"]): { label: string; tone: string } {
  switch (status) {
    case "awaiting_approval":
      return { label: "Pending", tone: "text-tertiary-container" };
    case "approved":
      return { label: "Ready", tone: "text-primary-fixed-dim" };
    case "active":
      return { label: "Active", tone: "text-primary-fixed-dim" };
    case "paused":
      return { label: "Paused", tone: "text-on-surface-variant" };
    case "error":
      return { label: "Error", tone: "text-error" };
    case "rejected":
      return { label: "Rejected", tone: "text-error" };
  }
}

function providerMeta(type: Connector["type"]): {
  label: string;
  icon: string;
  iconClass: string;
  iconBg: string;
} {
  switch (type) {
    case "google":
      return {
        label: "Google Calendar",
        icon: "calendar_today",
        iconClass: "text-tertiary-container",
        iconBg: "bg-tertiary-container/10 border-tertiary-container/30",
      };
    case "outlook_pst":
      return {
        label: "Local Outlook",
        icon: "desktop_windows",
        iconClass: "text-primary-fixed-dim",
        iconBg: "bg-primary-container/10 border-primary-container/20",
      };
    case "graph":
      return {
        label: "Microsoft 365",
        icon: "cloud",
        iconClass: "text-primary-fixed-dim",
        iconBg: "bg-primary-container/10 border-primary-container/20",
      };
    case "ios":
      return { label: "iOS Calendar", icon: "phone_iphone", iconClass: "text-secondary", iconBg: "bg-secondary/10 border-secondary/30" };
    case "android":
      return { label: "Android Calendar", icon: "phone_android", iconClass: "text-secondary", iconBg: "bg-secondary/10 border-secondary/30" };
  }
}

function describeOAuthError(code: string, params: URLSearchParams): string {
  switch (code) {
    case "google_not_configured":
      return "Google sign-in isn't set up on the hub yet. Email appsdo.ind@gmail.com to ask the admin to finish the Google Cloud Console setup, then try again.";
    case "not_approved":
      return "This Gmail isn't approved by the admin yet. Once approved on the Approvals page, you can click Connect now.";
    case "connector_not_found":
      return "We couldn't find that request — it may have been deleted. Request the Gmail again from this page.";
    case "missing_connector_id":
      return "Internal error: the Connect link was malformed. Refresh and try again.";
    case "connector_missing_email":
      return "This request is missing its email address — can't continue. Delete it and request the Gmail again.";
    case "state_mismatch":
    case "state_expired":
      return "OAuth session expired (took longer than 10 minutes). Click Connect now again.";
    case "token_exchange_failed":
      return "Google rejected the authorisation code. The admin needs to verify the OAuth client redirect URI matches https://calsync.appsdo.in/api/v1/connectors/google/oauth/callback exactly.";
    case "userinfo_failed":
      return "Couldn't read your email from Google. Try again.";
    case "email_mismatch": {
      const expected = params.get("expected") ?? "?";
      const got = params.get("got") ?? "?";
      return `You signed in to Google with ${got}, but the request was for ${expected}. Sign out of Google in another tab, then click Connect now again.`;
    }
    case "missing_code":
    case "bad_callback":
      return "OAuth callback was malformed. Click Connect now again.";
    case "access_denied":
      return "You denied permission on Google's screen. No connection was made.";
    default:
      return `Google OAuth error: ${code}`;
  }
}
